import { motion } from "framer-motion";
import { ArrowUpRight, Landmark } from "lucide-react";
import { EASE, Overline, Reveal, scrollToId } from "./Reveal";

const PROOFS = [
  {
    title: "Single contracting framework",
    body: "One commercial relationship across all five entities — procurement once, not five times.",
  },
  {
    title: "Agreed KPIs, 30-day reviews",
    body: "Every engagement runs against agreed measures with a standing 30-day review cycle.",
  },
  {
    title: "Auditable delivery standards",
    body: "ISO and GDPR-aligned practices, documented processes and assessment audit trails.",
  },
  {
    title: "Public sector experience",
    body: "Mission-critical talent and technology delivery across citizen services and shared services.",
  },
];

export default function GovSection() {
  return (
    <section
      id="group"
      data-testid="government-section"
      className="relative scroll-mt-24 overflow-hidden border-y border-white/10 bg-surface/40"
    >
      <div
        className="pointer-events-none absolute -left-40 top-1/2 h-[560px] w-[560px] -translate-y-1/2 rounded-full bg-blue-500/5 blur-3xl"
        aria-hidden
      />
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-12 lg:px-8 lg:py-32">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline className="flex items-center gap-2">
              <Landmark className="h-3.5 w-3.5" /> Enterprise & Government
            </Overline>
            <h2 className="mt-6 font-heading text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              Accountable delivery for buyers who answer to{" "}
              <span className="text-gold">everyone.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-gray-300">
              Enterprise procurement teams and public sector buyers share one problem:
              too many vendors, too many handovers, no single point of accountability.
              Dignifyd replaces the vendor stack with one relationship, one governance
              standard and one delivery backbone.
            </p>
            <button
              data-testid="gov-cta"
              onClick={() => scrollToId("#contact")}
              className="group mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:text-blue-300"
            >
              Discuss a mandate
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
            {PROOFS.map((p, i) => (
              <motion.div
                key={p.title}
                data-testid={`gov-proof-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
                className="group bg-ink p-8 transition-colors duration-500 hover:bg-surface"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 transition-colors duration-300 group-hover:text-gold">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-heading text-xl font-medium tracking-tight text-white">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
